import React, { ReactNode, useMemo, useState } from 'react';
import { Button, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { KeyboardArrowDown, Login } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { HeaderAppBar } from './app-bar';
import { Auth, AuthProps } from '../../components/auth/auth';
import { useMuiSwitchableTheme } from '../../theme';

export interface SiteHeaderNavLink {
  label: string;
  href?: string;
  onClick?: () => void;
  alerts?: number;
  icon?: ReactNode;
  dropDownItems?: {
    label: string;
    href?: string;
    onClick?: () => void;
    alerts?: number;
    external?: boolean;
    /** Module ID required for this nav item to be visible */
    requiredModule?: string;
    /** User roles required for this nav item to be visible */
    requiredRoles?: string[];
  }[];
  /** Module ID required for this nav item to be visible */
  requiredModule?: string;
  /** User roles required for this nav item to be visible */
  requiredRoles?: string[];
}

interface SiteHeaderProps<TUser> {
  title: ReactNode;
  navLinks: SiteHeaderNavLink[];
  loginFn: () => Promise<void>;
  logoutFn: () => Promise<void>;
  isProduction?: boolean;
  env?: string;
  user?: TUser;
  logo: ReactNode;
  dropdownItems?: AuthProps<TUser>['dropdownItems'];
  vendorName: string;
  persistentLeftNav?: boolean;
  globalSearchElement?: ReactNode | null;
}

const SignInButtons: React.FC<{ loginFn: () => Promise<void> }> = ({
  loginFn,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { navCollapsed, navMode, isTablet, themeMode } =
    useMuiSwitchableTheme();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const oneTimePasswordLogin = (connection: 'email' | 'sms') => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = `${window.location.pathname}${window.location.search}`;
    searchParams.set('redirect', redirect);
    searchParams.set(connection === 'email' ? 'useEmail' : 'usePhone', 'true');
    navigate(`/login?${searchParams.toString()}`, {
      replace: true,
      unstable_flushSync: true, // force sync to update search params immediately
    });
  };

  const showIconOnly = useMemo(
    () => navMode === 'left' && navCollapsed && !isTablet,
    [navMode, navCollapsed, isTablet],
  );

  return (
    <Stack
      direction={navMode === 'top' && !isTablet ? 'row' : 'column'}
      spacing={2}
    >
      {!showIconOnly ? (
        <Button
          onClick={loginFn}
          variant="contained"
          sx={{ backgroundColor: 'primary.dark' }}
        >
          Log In
        </Button>
      ) : (
        <IconButton onClick={loginFn} sx={{ color: 'primary.contrastText' }}>
          <Login />
        </IconButton>
      )}
    </Stack>
  );
};

export function SiteHeader<
  TUser extends { first_name?: string; picture_url?: string | null },
>({
  navLinks,
  loginFn,
  logoutFn,
  user,
  dropdownItems = [],
  vendorName,
  env = '',
  globalSearchElement = null,
}: SiteHeaderProps<TUser>) {
  return (
    <HeaderAppBar
      links={navLinks}
      baseColor="primary"
      env={env}
      globalSearchElement={globalSearchElement}
      authComponent={
        user ? (
          <Auth<TUser>
            user={user}
            logoutFn={logoutFn}
            dropdownItems={dropdownItems}
            vendorName={vendorName}
          />
        ) : (
          <SignInButtons loginFn={loginFn} />
        )
      }
    />
  );
}
